import { Button, Switch, TextField, Tooltip } from "@mui/material";
import { GridColDef, GridRowModel, GridRowModes, GridRowModesModel, GridToolbarContainer, GridToolbarQuickFilter, GridSortModel, GridPaginationModel, GridEventListener, GridRowEditStopReasons } from "@mui/x-data-grid";
import { SettingGrid } from "../../../components/ManageSettingComponents/SettingGrid";
import { DataTypeDetail } from "../../../models/DataTypeDetail";
import { useState } from "react";
import { toast } from "react-toastify";
import DeleteValidationModal from "../../../components/ManageSettingComponents/DeleteValidationModal";
import { GRID_ACTION_DELETE_ICON, GRID_READ_ONLY } from "../../../components/GridUtilityComponents";
import { Permissions } from "../../../models/Enum";
import SessionService from "../../../services/SessionService";
import { Add } from "@mui/icons-material";
import api from "../../../services/ApiService";
import { ApiResponse } from "../../../models/ApiResponse";
import { SessionDetails } from "../../../models/SessionDetails";
import { EDIT_GRID_TOOLTIP } from "../../Products/EditItem/Constants";

export default function DataTypeSetting({ getDataTypeList, rowModesModel, setRowModesModel, cellErrors, setCellErrors, handleSubmit, submitValues, editingRows, setEditingRows, dataField, dataTypeRows, setDataTypeRows, apiRef }) {
    const manageAppSetting = SessionService.getInstance().hasPermission(Permissions.ManageAppSettings);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedDataType, setSelectedDataType] = useState<DataTypeDetail>();
    const [gridSortModel, setGridSortModel] = useState<GridSortModel>([
        {
            field: "id",
            sort: "desc",
        },
    ]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 25,
    });

    const handleTabNavigation = (event: React.KeyboardEvent, params: any, nextField: string) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            // Get the current field name
            const currentField = params.field;
            // Define the field order for navigation
            const fieldOrder = ['description', 'leagoCode', 'pimKey', 'jdeCode'];
            const currentIndex = fieldOrder.indexOf(currentField);
            
            if (event.shiftKey) {
                // Move to previous field if not on the first field
                const previousField = currentIndex > 0 ? fieldOrder[currentIndex - 1] : fieldOrder[fieldOrder.length - 1];
                const previousFieldElement = params.api.getCellElement(params.id, previousField);
                if (previousFieldElement) {
                    const input = previousFieldElement.querySelector('input');
                    if (input) {
                        input.focus();
                    }
                }
            } else {
                // Move to the next field in the same row
                const nextFieldElement = params.api.getCellElement(params.id, nextField);
                if (nextFieldElement) {
                    const input = nextFieldElement.querySelector('input');
                    if (input) {
                        input.focus();
                    }
                }
            }
        }
    };

    const DataTypeColumns: GridColDef[] = [
        { field: "id", headerName: "Id", width: 100 },
        { field: "isEditing", headerName: "Editing", width: 100 },
        {
            field: "description",
            sortable: !editingRows || editingRows?.length <= 0,
            headerName: "Description",
            editable: !!manageAppSetting,
            disableColumnMenu: true,
            type: "string",
            width: 380,
            cellClassName: (params) =>
                cellErrors[params.id]?.description ? 'cell-error' : '',
            renderCell: (params) => {
                const errorMsg = cellErrors[params.id]?.description;
                return errorMsg ? (
                    <Tooltip title={errorMsg}>
                        <div className="grid-tooltip-error">
                            {params.value}
                        </div>
                    </Tooltip>
                ) : (
                    <Tooltip title={manageAppSetting ? EDIT_GRID_TOOLTIP : undefined}>
                        <div className="grid-no-error-text">{params.value}</div>
                    </Tooltip>
                );
            },
            renderEditCell: (params) => {
                const errorMsg = cellErrors[params.id]?.description;
                return (
                    <Tooltip title={errorMsg || ''}>
                        <TextField
                            value={params.value || ''}
                            onChange={event => onChangeHandler(params, event)}
                            size="small"
                            fullWidth
                            onKeyUp={(event) => handleTabNavigation(event, params, 'leagoCode')}
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: "leagoCode",
            sortable: !editingRows || editingRows?.length <= 0,
            headerName: "LEAGO Code",
            editable: !!manageAppSetting && dataField?.field !== "CubeMultiplier",
            disableColumnMenu: true,
            type: "string",
            width: 330,
            cellClassName: (params) =>
                cellErrors[params.id]?.leagoCode ? 'cell-error' : '',
            renderCell: (params) => {
                const errorMsg = cellErrors[params.id]?.leagoCode;
                return errorMsg ? (
                    <Tooltip title={errorMsg}>
                        <div className="grid-tooltip-error">
                            {params.value}
                        </div>
                    </Tooltip>
                ) : (
                    <Tooltip title={manageAppSetting && dataField?.field !== "CubeMultiplier" ? EDIT_GRID_TOOLTIP : undefined}>
                        <div className="grid-no-error-text">{params.value}</div>
                    </Tooltip>
                );
            },
            renderEditCell: (params) => {
                const errorMsg = cellErrors[params.id]?.leagoCode;
                return (
                    <Tooltip title={errorMsg || ''}>
                        <TextField
                            value={params.value || ''}
                            onChange={event => onChangeHandler(params, event)}
                            size="small"
                            fullWidth
                            onKeyUp={(event) => handleTabNavigation(event, params, 'pimKey')}
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: "pimKey",
            sortable: !editingRows || editingRows?.length <= 0,
            headerName: "Pim Key",
            editable: !!manageAppSetting && dataField?.field !== "CubeMultiplier",
            disableColumnMenu: true,
            type: "string",
            width: 330,
            cellClassName: (params) =>
                cellErrors[params.id]?.pimKey ? 'cell-error' : '',
            renderCell: (params) => {
                const errorMsg = cellErrors[params.id]?.pimKey;
                return errorMsg ? (
                    <Tooltip title={errorMsg}>
                        <div className="grid-tooltip-error">
                            {params.value}
                        </div>
                    </Tooltip>
                ) : (
                    <Tooltip title={manageAppSetting && dataField?.field !== "CubeMultiplier" ? EDIT_GRID_TOOLTIP : undefined}>
                        <div className="grid-no-error-text">{params.value}</div>
                    </Tooltip>
                );
            },
            renderEditCell: (params) => {
                const errorMsg = cellErrors[params.id]?.pimKey;
                return (
                    <Tooltip title={errorMsg || ''}>
                        <TextField
                            value={params.value || ''}
                            onChange={event => onChangeHandler(params, event)}
                            size="small"
                            fullWidth
                            onKeyUp={(event) => handleTabNavigation(event, params, 'jdeCode')}
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: "jdeCode",
            sortable: !editingRows || editingRows?.length <= 0,
            headerName: "JDE Code",
            editable: !!manageAppSetting && dataField?.field !== "CubeMultiplier",
            disableColumnMenu: true,
            type: "string",
            width: 330,
            cellClassName: (params) =>
                cellErrors[params.id]?.jdeCode ? 'cell-error' : '',
            renderCell: (params) => {
                const errorMsg = cellErrors[params.id]?.jdeCode;
                return errorMsg ? (
                    <Tooltip title={errorMsg}>
                        <div className="grid-tooltip-error">
                            {params.value}
                        </div>
                    </Tooltip>
                ) : (
                    <Tooltip title={manageAppSetting && dataField?.field !== "CubeMultiplier" ? EDIT_GRID_TOOLTIP : undefined}>
                        <div className="grid-no-error-text">{params.value}</div>
                    </Tooltip>
                );
            },
            renderEditCell: (params) => {
                const errorMsg = cellErrors[params.id]?.jdeCode;
                return (
                    <Tooltip title={errorMsg || ''}>
                        <TextField
                            value={params.value || ''}
                            onChange={event => onChangeHandler(params, event)}
                            size="small"
                            fullWidth
                            onKeyUp={(event) => handleTabNavigation(event, params, "description")}
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: "isActive",
            headerName: "Is Active",
            width: 100,
            disableColumnMenu: true,
            sortable: !editingRows || editingRows?.length <= 0,
            renderCell: (params: any) => {
                if (params.row.readOnlyField)
                    return GRID_READ_ONLY();

                return (
                    <Switch
                        checked={params.value}
                        onChange={async (e) => {
                            if (typeof params.row.id === "string") {
                                const newDataTypes = dataTypeRows.map(item => item.id === params.row.id ? { ...item, isActive: e.target.checked } : item);
                                setDataTypeRows(newDataTypes);
                                validateRows(newDataTypes);
                            } else {
                                await updateDataType(params.row, e.target.checked);
                            }
                        }}
                        color="primary"
                        inputProps={{ "aria-label": "controlled" }}
                        disabled={!manageAppSetting}
                    />
                );
            }
        },
        {
            field: "actions", sortable: false, type: "actions", disableColumnMenu: true, headerName: "Actions", width: 100,
            getActions: (params: any) => {
                if (params.row.readOnlyField) {
                    return [
                        GRID_READ_ONLY()
                    ];
                }
                return [
                    GRID_ACTION_DELETE_ICON(params, handleDeleteModalOpen)
                ];
            }
        }
    ];

    //updating datafield only for the switch in active 
    const updateDataType = async (row: DataTypeDetail, isActive: boolean) => {
        const data = {
            ...row,
            isActive: isActive
        };
        try {
            api
                .put<ApiResponse<boolean>>("/Picklist/cvl-option", data)
                .then(async (response) => {
                    if (response.isSuccess) {
                        toast.success(response.message);
                        setDataTypeRows(dataTypeRows.map(item => item.id === row.id ? { ...item, isActive } : item));
                    } else {
                        throw new Error(response.message);
                    }
                })
                .catch(() => { });
        } catch (err: any) {
            toast.error(err.message);
        }
    };

    const deleteDataType = async () => {
        try {
            api
                .delete<ApiResponse<SessionDetails>>(`/Picklist/cvl-option/${selectedDataType.id}`)
                .then(async (response) => {
                    if (response.isSuccess) {
                        setDeleteModalOpen(false);
                        toast.success(response.message);
                        const updatedDataTypes = dataTypeRows.filter(item => item.id !== selectedDataType.id);
                        setDataTypeRows(updatedDataTypes);
                        validateRows(updatedDataTypes);
                    } else {
                        throw new Error(response.message);
                    }
                })
                .catch(() => { });
        } catch (err: any) {
            toast.error(err.message);
        }
    };

    const onChangeHandler = (params: any, event: any) => {
        params.api.setEditCellValue({ id: params.id, field: params.field, value: event?.target?.value }, event);
        const updatedDataTypes = dataTypeRows.map(item => item.id === params.id ? { ...item, [params.field]: event?.target?.value } : item);
        setDataTypeRows(updatedDataTypes);
        return validateRows(updatedDataTypes);
    };

    const handleDeleteModalOpen = (gridParams: any) => {
        setDeleteModalOpen(true);
        setSelectedDataType(gridParams.row);
    };

    const handleDeleteDataType = async () => {
        if (selectedDataType?.id) {
            if (dataTypeRows?.length === 1) {
                setDeleteModalOpen(false);
                return toast.error("Cannot delete the last cvl option");
            }
            if (typeof selectedDataType.id === "string") {
                const updatedDataTypes = dataTypeRows.filter(item => item.id !== selectedDataType.id);
                setDataTypeRows(updatedDataTypes);
                setEditingRows(editingRows.filter(item => item.id !== selectedDataType.id));
                setDeleteModalOpen(false);
                validateRows(updatedDataTypes);
            } else {
                await deleteDataType();
            }
        }
    };

    const handleCloseModal = () => {
        setDeleteModalOpen(false);
    };

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleDoubleClick = async (params) => {
        if (dataField?.field === "CubeMultiplier" && params.field !== "description") {
            return;
        }
        if (params.field && rowModesModel[params.row.id]?.mode !== GridRowModes.Edit) {
            setEditingRows([...editingRows, { ...params.row, isEditing: true }]);
            const newDataTypes = dataTypeRows.map(item => item.id === params.row.id ? { ...item, isEditing: true } : item);
            setDataTypeRows(newDataTypes);
            setRowModesModel((prevModel) => {
                const newModel = { ...prevModel };

                // Set the clicked row to "Edit" mode
                newModel[params.row.id] = { mode: GridRowModes.Edit };

                return newModel;
            });
        }
    };

    const gridToolbar = () => (
        <GridToolbarContainer>
            <div className="grid-toolbar">
                <GridToolbarQuickFilter />
                {dataField?.field !== "CubeMultiplier" && <Button onClick={handleAddRow} variant="contained"
                >
                    <Add />
                    Add Row
                </Button>}
            </div>
        </GridToolbarContainer>
    );

    const handleAddRow = () => {
        const uniqueErrorMsg = validateRows(dataTypeRows);
        if (Object.keys(uniqueErrorMsg).length > 0) {
            toast.error("Please fix the errors before adding a new row");
        } else {
            let uniqueId: string = "";
            const secondValue = new Date().getTime();
            if (!dataTypeRows || dataTypeRows?.length <= 0) {
                uniqueId = "1-" + secondValue;
            } else {
                const maxValue: number | string = dataTypeRows?.map(item => item.id)?.reduce((a, b) => String(a) > String(b) ? a : b);
                // temporary id for the new row
                if (maxValue && isNaN(Number(maxValue))) {
                    const splitValue = maxValue.toString()?.split('-');
                    const firstValue = splitValue[0];
                    uniqueId = (Number(firstValue) + 1) + "-" + secondValue;
                } else if (maxValue && !isNaN(Number(maxValue))) {
                    uniqueId = (Number(maxValue) + 1) + "-" + secondValue;
                } else {
                    uniqueId = "1-" + secondValue;
                }
            }

            // Create a fresh empty data object each time
            const emptyData = {
                description: "",
                leagoCode: "",
                isActive: true,
                jdeCode: "",
                pimKey: "",
                readOnlyField: false,
                id: uniqueId,
                isEditing: true
            };

            // Update all states with the new row
            setDataTypeRows(prevRows => [emptyData, ...prevRows]);
            // Focus on the input element within the cell
            setEditingRows(prevRows => [...prevRows, emptyData]);
            apiRef.current.setCellFocus(emptyData.id, "description");
            // Reset row modes
            setRowModesModel((prevModel) => {
                const newModel = { ...prevModel };
                newModel[emptyData.id] = { mode: GridRowModes.Edit };
                setTimeout(() => {
                    const cellElement = document.querySelector(`[data-id="${emptyData.id}"] [data-field="description"]`);
                    if (cellElement) {
                        const inputElement = cellElement.querySelector('input');
                        if (inputElement) {
                            inputElement.focus();
                            apiRef.current.scrollToIndexes({
                                rowIndex: 0,
                                colIndex: 0,
                            });
                        }
                    }
                }, 200);
                return newModel;
            });
        }
    };

    /**
     * Validates an array of objects for required fields and uniqueness.
     * @param {Array} arr - Array of objects with id, description, leagoCode, pimKey, jdeCode
     * @returns {Object} errors - { [id]: { fieldName: "error message", ... } }
     */
    function validateRows(arr) {
        const errors = {};

        // 1. Build value-to-ids maps for each field
        const valueToIds = {
            leagoCode: {},
            jdeCode: {},
            pimKey: {},
        };

        arr.forEach(obj => {
            const leago = obj.leagoCode?.trim().toLowerCase() || "";
            const jde = obj.jdeCode?.trim().toLowerCase() || "";
            const pim = obj.pimKey?.trim().toLowerCase() || "";

            if (leago) valueToIds.leagoCode[leago] = (valueToIds.leagoCode[leago] || []).concat(obj.id);
            if (jde) valueToIds.jdeCode[jde] = (valueToIds.jdeCode[jde] || []).concat(obj.id);
            if (pim) valueToIds.pimKey[pim] = (valueToIds.pimKey[pim] || []).concat(obj.id);
        });

        // 2. Find all duplicate values for each field
        const duplicates = {
            leagoCode: new Set(Object.keys(valueToIds.leagoCode).filter(val => valueToIds.leagoCode[val].length > 1)),
            jdeCode: new Set(Object.keys(valueToIds.jdeCode).filter(val => valueToIds.jdeCode[val].length > 1)),
            pimKey: new Set(Object.keys(valueToIds.pimKey).filter(val => valueToIds.pimKey[val].length > 1)),
        };

        // 3. Validate each row
        arr.forEach(obj => {
            const objErrors: any = {};

            // Required fields
            if (!obj.description || obj.description.trim() === "") {
                objErrors.description = "Description is required";
            }
            if (!obj.leagoCode || obj.leagoCode.trim() === "") {
                objErrors.leagoCode = "LEAGO Code is required";
            }

            // Uniqueness
            const leago = obj.leagoCode?.trim().toLowerCase() || "";
            const jde = obj.jdeCode?.trim().toLowerCase() || "";
            const pim = obj.pimKey?.trim().toLowerCase() || "";

            if (leago && duplicates.leagoCode.has(leago)) {
                objErrors.leagoCode = "LEAGO Code must be unique";
            }
            if (jde && duplicates.jdeCode.has(jde)) {
                objErrors.jdeCode = "JDE Code must be unique";
            }
            if (pim && duplicates.pimKey.has(pim)) {
                objErrors.pimKey = "Pim Key must be unique";
            }

            if (Object.keys(objErrors).length > 0) {
                errors[obj.id] = objErrors;
            }
        });

        setCellErrors(errors);
        return errors;
    }

    function getFirstErrorMessage(errors) {
        for (const rowId in errors) {
            if (errors.hasOwnProperty(rowId)) {
                const fieldErrors = errors[rowId];
                for (const field in fieldErrors) {
                    if (fieldErrors.hasOwnProperty(field)) {
                        return fieldErrors[field];
                    }
                }
            }
        }
        return null; // or undefined, or a default message
    }

    // Processes the row update by handling changes made to a row's data
    const processRowUpdate = async (newRow: GridRowModel) => {
        if (!newRow.id) {
            return Promise.reject(new Error("Row must have an id"));
        }

        const updatedDataTypes = dataTypeRows.map(item => item.id === newRow.id ? { ...newRow, id: item.id } : item);
        const uniqueErrorMsg = validateRows(updatedDataTypes);
        const errorIds = Object.keys(uniqueErrorMsg);
        if (Object.keys(uniqueErrorMsg).length > 0) {
            let updatedEditingRows = editingRows.map(item => item.id === newRow.id ? { ...item, isEditing: true } : item);
            updatedEditingRows = updatedEditingRows.filter(item => errorIds.includes(item.id.toString()));
            setEditingRows([...updatedEditingRows]);
            setDataTypeRows(updatedDataTypes.map(item => !errorIds.includes(item.id.toString()) ? { ...item, isEditing: false } : item));
            setRowModesModel((prevModel) => {
                const newModel = { ...prevModel };

                // Set all rows to "View" mode
                Object.keys(newModel).forEach((rowId) => {
                    if (errorIds.includes(rowId.toString())) {
                        newModel[rowId] = { mode: GridRowModes.Edit };
                    } else {
                        newModel[rowId] = { mode: GridRowModes.View };
                    }
                });
                return newModel;
            });
            if (!errorIds.includes(newRow.id.toString())) {
                return newRow;
            } else {
                return toast.error(getFirstErrorMessage(uniqueErrorMsg));
            }
        }

        setEditingRows([]);
        setDataTypeRows(updatedDataTypes.map(item => item.id === newRow.id ? { ...item, isEditing: false } : item));
        setRowModesModel((prevModel) => {
            const newModel = { ...prevModel };
            // Set all rows to "View" mode
            Object.keys(newModel).forEach((rowId) => {
                newModel[rowId] = { mode: GridRowModes.View };
            });
            return newModel;
        });
        if (submitValues) {
            handleSubmit(updatedDataTypes);
        }
        return { ...newRow, id: newRow.id };
    };

    const handleSortModelChange = (sortModel: GridSortModel) => {
        setGridSortModel(sortModel);
    };

    const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
        setPaginationModel(paginationModel);
    };

    return (
        <div className="data-type-grid">
            <DeleteValidationModal
                addModalOpen={deleteModalOpen}
                handleCloseModal={handleCloseModal}
                deleteSubmit={() => handleDeleteDataType()}
                fieldIdentifier={"CVL Option"}
            />
            <SettingGrid
                data={dataTypeRows?.length > 0 ? dataTypeRows : []}
                column={DataTypeColumns}
                sortByField="id"
                sortByOrder="desc"
                gridSortModel={gridSortModel}
                onSortModelChange={handleSortModelChange}
                gridPaginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                columnVisibility={{
                    actions: manageAppSetting && dataField?.field !== "CubeMultiplier",
                    isActive: dataField?.field !== "CubeMultiplier",
                    isEditing: false,
                }}
                gridToolbar={gridToolbar}
                editMode="row"
                handleDoubleClick={handleDoubleClick}
                rowModesModel={rowModesModel}
                handleRowModesModelChange={handleRowModesModelChange}
                processRowUpdate={processRowUpdate}
                apiRef={apiRef}
            />
        </div>
    );
};